import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { SEO } from 'src/components/SEO';
import { PublicMarketingFooter } from 'src/components/PublicMarketingFooter';
import { PublicScrollToTopFab } from 'src/components/PublicScrollToTopFab';
import { Logo } from 'src/components/Logo';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import {
  BLOG_CATEGORY_ORDER,
  getBlogCategoryLabel,
  postMatchesBlogCategory,
  type BlogCategoryFilterId,
} from 'src/data/blog/blogCategories';
import { getBlogPostPath } from 'src/data/blog/paths';
import type { BlogLocale } from 'src/data/blog/types';
import { getSortedPostsForBlogHub } from 'src/data/blog/selectors';
import { blogHeroHeadlineForSeo, getBlogCopy } from './blogCopy';

const SITE_ORIGIN = 'https://app.kipify.io';

const PageRoot = styled(Box)(() => ({
  minHeight: '100vh',
  width: '100%',
  color: '#fff',
  background:
    'radial-gradient(circle at 10% 0%, rgba(30, 237, 159, 0.18), transparent 26%), radial-gradient(circle at 100% 12%, rgba(1, 128, 148, 0.2), transparent 30%), linear-gradient(180deg, #071017 0%, #0b0e13 40%, #080c10 100%)',
  position: 'relative',
  overflowX: 'clip',
}));

const accent = '#1EED9F';
const muted = '#aab4c3';

const GlassPanel = styled(Box)(() => ({
  borderRadius: 20,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background:
    'linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02))',
  boxShadow: '0 20px 64px rgba(0, 0, 0, 0.35)',
  backdropFilter: 'blur(16px)',
}));

export interface BlogHomeProps {
  locale: BlogLocale;
}

function buildBlogJsonLd(args: {
  locale: BlogLocale;
  canonical: string;
  h1: string;
  description: string;
  breadcrumbHome: string;
  breadcrumbBlog: string;
}) {
  const inLanguage = args.locale === 'it' ? 'it-IT' : 'en-US';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: 'Kipify',
        url: SITE_ORIGIN,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_ORIGIN}/images/hero-header-kipify.png`,
        },
        sameAs: ['https://www.linkedin.com/company/kipify'],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_ORIGIN}/#website`,
        url: SITE_ORIGIN,
        name: 'Kipify',
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
        inLanguage: ['it-IT', 'en-US'],
      },
      {
        '@type': 'Blog',
        '@id': `${args.canonical}#blog`,
        url: args.canonical,
        name: 'Kipify Blog',
        headline: args.h1,
        description: args.description,
        inLanguage,
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
        isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: args.breadcrumbHome,
            item: SITE_ORIGIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: args.breadcrumbBlog,
            item: args.canonical,
          },
        ],
      },
    ],
  };
}

const BLOG_CARD_FALLBACK_IMAGE = '/images/hero-header-kipify.png';

function ArticleCard(props: {
  post: import('src/data/blog/types').BlogPost;
  /** Lingua della pagina hub (per badge se il post è nell’altra lingua). */
  pageLocale: BlogLocale;
  readCta: string;
}) {
  const { post, pageLocale, readCta } = props;
  const to = getBlogPostPath(post.slug, post.language);
  const dateLocale = post.language === 'it' ? itLocale : enUS;
  const showLangBadge = post.language !== pageLocale;
  const coverSrc = post.cardImageSrc ?? BLOG_CARD_FALLBACK_IMAGE;
  const coverLabel = `${readCta}: ${post.title}`;

  return (
    <GlassPanel
      component="article"
      sx={{
        p: 0,
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <MuiLink
        component={RouterLink}
        to={to}
        underline="none"
        aria-label={coverLabel}
        sx={{
          display: 'block',
          flexShrink: 0,
          lineHeight: 0,
          '&:focus-visible': {
            outline: `2px solid ${accent}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box
          component="img"
          src={coverSrc}
          alt={post.cardImageAlt ?? ''}
          loading="lazy"
          decoding="async"
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            display: 'block',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        />
      </MuiLink>
      <Stack
        sx={{
          p: { xs: 2.25, sm: 2.5 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ mb: 0.5 }}
        >
          <Typography
            variant="overline"
            sx={{ color: accent, letterSpacing: '0.1em', fontWeight: 700 }}
          >
            {post.category}
          </Typography>
          {showLangBadge ? (
            <Chip
              label={post.language === 'en' ? 'EN' : 'IT'}
              size="small"
              sx={{
                height: 22,
                fontWeight: 800,
                fontSize: 11,
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#e8eef7',
              }}
            />
          ) : null}
        </Stack>
        <Typography
          component="h3"
          variant="h6"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            mt: 0.5,
            mb: 1,
            lineHeight: 1.3,
            fontSize: { xs: '1.05rem', sm: '1.12rem', md: '1.18rem' },
          }}
        >
          <MuiLink
            component={RouterLink}
            to={to}
            underline="hover"
            color="inherit"
            sx={{
              '&:focus-visible': {
                outline: `2px solid ${accent}`,
                outlineOffset: 3,
                borderRadius: 0.5,
              },
            }}
          >
            {post.title}
          </MuiLink>
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: muted, display: 'block', mb: 1.5 }}
        >
          {format(parseISO(post.publishedAt), 'PP', { locale: dateLocale })}
          {post.readingTimeMinutes != null
            ? ` · ${post.readingTimeMinutes} min`
            : ''}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: muted, lineHeight: 1.65, flexGrow: 1, mb: 2 }}
        >
          {post.excerpt}
        </Typography>
        <Button
          component={RouterLink}
          to={to}
          variant="text"
          sx={{
            alignSelf: 'flex-start',
            color: accent,
            fontWeight: 700,
            textTransform: 'none',
            px: 0,
            '&:focus-visible': {
              outline: `2px solid ${accent}`,
              outlineOffset: 2,
            },
          }}
        >
          {readCta}
        </Button>
      </Stack>
    </GlassPanel>
  );
}

export const BlogHome: React.FC<BlogHomeProps> = ({ locale }) => {
  const copy = getBlogCopy(locale);
  const canonical =
    locale === 'it'
      ? `${SITE_ORIGIN}/${ROUTE_SOURCES.BLOG_IT}`
      : `${SITE_ORIGIN}/${ROUTE_SOURCES.BLOG}`;
  const listing = useMemo(() => getSortedPostsForBlogHub(locale), [locale]);
  const sorted = listing.posts;
  const [category, setCategory] = useState<BlogCategoryFilterId>('all');

  const filtered = useMemo(
    () => sorted.filter((p) => postMatchesBlogCategory(p.category, category)),
    [sorted, category],
  );

  const structuredData = useMemo(
    () =>
      buildBlogJsonLd({
        locale,
        canonical,
        h1: blogHeroHeadlineForSeo(copy),
        description: copy.seoDescription,
        breadcrumbHome: copy.breadcrumbHome,
        breadcrumbBlog: copy.breadcrumbBlog,
      }),
    [
      locale,
      canonical,
      copy.h1Line1,
      copy.h1Line2,
      copy.seoDescription,
      copy.breadcrumbHome,
      copy.breadcrumbBlog,
    ],
  );

  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = locale === 'it' ? 'it' : 'en';
    return () => {
      document.documentElement.lang = prev;
    };
  }, [locale]);

  const alternates = useMemo(
    () => [
      { hrefLang: 'en', href: `${SITE_ORIGIN}/blog` },
      { hrefLang: 'it', href: `${SITE_ORIGIN}/${ROUTE_SOURCES.BLOG_IT}` },
      { hrefLang: 'x-default', href: `${SITE_ORIGIN}/blog` },
    ],
    [],
  );

  const emptyCategory =
    locale === 'it'
      ? 'Nessun articolo in questa categoria.'
      : 'No articles in this category yet.';

  return (
    <PageRoot>
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        canonical={canonical}
        ogLocale={copy.ogLocale}
        ogImage={`${SITE_ORIGIN}/images/hero-header-kipify.png`}
        robots="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        alternates={alternates}
        structuredData={structuredData}
      />

      <Box
        component="header"
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <Container maxWidth="lg" sx={{ py: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <MuiLink
                component={RouterLink}
                to="/"
                underline="none"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  color: '#fff',
                  borderRadius: 1,
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 4,
                  },
                }}
                aria-label="Kipify home"
              >
                <Logo variant="default" size="small" />
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    fontSize: '1.15rem',
                  }}
                >
                  Kipify
                </Typography>
              </MuiLink>
            </Stack>

            <Stack
              component="nav"
              direction="row"
              spacing={1.5}
              alignItems="center"
              aria-label={locale === 'it' ? 'Lingua' : 'Language'}
            >
              <MuiLink
                component={RouterLink}
                to={`/${ROUTE_SOURCES.BLOG}`}
                underline="none"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 14,
                  color: locale === 'en' ? '#0b0e13' : muted,
                  bgcolor: locale === 'en' ? accent : 'transparent',
                  border:
                    locale === 'en'
                      ? 'none'
                      : '1px solid rgba(255,255,255,0.2)',
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {copy.langEn}
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to={`/${ROUTE_SOURCES.BLOG_IT}`}
                underline="none"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 14,
                  color: locale === 'it' ? '#0b0e13' : muted,
                  bgcolor: locale === 'it' ? accent : 'transparent',
                  border:
                    locale === 'it'
                      ? 'none'
                      : '1px solid rgba(255,255,255,0.2)',
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {copy.langIt}
              </MuiLink>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        component="nav"
        aria-label="Breadcrumb"
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ color: muted }}>
            <MuiLink
              component={RouterLink}
              to="/"
              color="inherit"
              underline="hover"
              sx={{
                '&:focus-visible': {
                  outline: `2px solid ${accent}`,
                  outlineOffset: 2,
                },
              }}
            >
              {copy.breadcrumbHome}
            </MuiLink>
            <Box component="span" sx={{ px: 1, opacity: 0.5 }}>
              /
            </Box>
            <Box component="span" sx={{ color: '#e8eef7' }}>
              {copy.breadcrumbBlog}
            </Box>
          </Typography>
        </Container>
      </Box>

      <Box component="main" id="main-content">
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Stack spacing={1.25} sx={{ maxWidth: 720 }}>
            <Typography
              component="h1"
              variant="h3"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.12,
                fontSize: { xs: '1.75rem', md: '2.35rem' },
              }}
            >
              <Box component="span" sx={{ display: 'block' }}>
                {copy.h1Line1}
              </Box>
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                {copy.h1Line2}
              </Box>
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: muted,
                fontWeight: 400,
                lineHeight: 1.55,
                fontSize: { xs: '1rem', md: '1.1rem' },
                mt: 1.5,
              }}
            >
              {copy.heroSub}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ pt: 2 }}
            >
              <Button
                component="a"
                href="#insights"
                variant="contained"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3,
                  bgcolor: accent,
                  color: '#061018',
                  boxShadow: '0 12px 40px rgba(30,237,159,0.25)',
                  '&:hover': { bgcolor: '#24f0a8' },
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 3,
                  },
                }}
              >
                {copy.primaryCta}
              </Button>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3,
                  borderColor: 'rgba(255,255,255,0.28)',
                  color: '#fff',
                  '&:hover': { borderColor: accent, color: accent },
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 3,
                  },
                }}
              >
                {copy.secondaryCta}
              </Button>
            </Stack>
          </Stack>
        </Container>

        <Container
          maxWidth="lg"
          id="insights"
          component="section"
          aria-label={copy.articlesTitle}
          sx={{ scrollMarginTop: '88px', pb: { xs: 6, md: 10 } }}
        >
          {sorted.length > 0 ? (
            <>
              <Typography
                component="h2"
                variant="h5"
                sx={{ fontWeight: 800, mb: 2 }}
              >
                {copy.articlesTitle}
              </Typography>
              {listing.mode === 'fallback' ? (
                <Typography
                  variant="body2"
                  sx={{ color: muted, lineHeight: 1.6, mb: 2, maxWidth: 720 }}
                >
                  {copy.fallbackGuestLanguageBanner}
                </Typography>
              ) : null}
              <Stack
                component="nav"
                direction="row"
                flexWrap="wrap"
                useFlexGap
                gap={1}
                sx={{ mb: 4 }}
                aria-label={locale === 'it' ? 'Categorie' : 'Categories'}
              >
                {BLOG_CATEGORY_ORDER.map((id) => (
                  <Button
                    key={id}
                    size="small"
                    onClick={() => setCategory(id)}
                    variant={category === id ? 'contained' : 'outlined'}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      fontWeight: 700,
                      px: 2,
                      borderColor: 'rgba(255,255,255,0.22)',
                      color: category === id ? '#061018' : '#e8eef7',
                      bgcolor: category === id ? accent : 'transparent',
                      '&:hover': {
                        borderColor: accent,
                        bgcolor:
                          category === id ? accent : 'rgba(30,237,159,0.08)',
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${accent}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {getBlogCategoryLabel(id, locale)}
                  </Button>
                ))}
              </Stack>

              {filtered.length === 0 ? (
                <Typography variant="body2" sx={{ color: muted }}>
                  {emptyCategory}
                </Typography>
              ) : (
                <>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: accent,
                        letterSpacing: '0.14em',
                        fontWeight: 700,
                      }}
                    >
                      {copy.featuredEyebrow}
                    </Typography>
                    <Typography
                      component="p"
                      variant="body2"
                      sx={{ color: muted, mt: 0.5, mb: 0, maxWidth: 720 }}
                    >
                      {copy.featuredTitle}
                    </Typography>
                  </Box>
                  <Grid container spacing={2.5}>
                    {filtered.map((p) => (
                      <Grid
                        item
                        xs={12}
                        md={filtered.length === 1 ? 12 : 6}
                        key={`${p.slug}-${p.language}-${p.publishedAt}`}
                      >
                        <ArticleCard
                          post={p}
                          pageLocale={locale}
                          readCta={copy.readArticleCta}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ color: muted, lineHeight: 1.65, maxWidth: 520 }}
            >
              {copy.emptyState}
            </Typography>
          )}
        </Container>

        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <GlassPanel sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              component="h2"
              variant="h5"
              sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}
            >
              {copy.productTitle}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: muted, lineHeight: 1.7, mb: 3 }}
            >
              {copy.productBody}
            </Typography>
            <Button
              component={RouterLink}
              to={`/${ROUTE_SOURCES.REGISTER}`}
              variant="contained"
              size="large"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                px: 3,
                bgcolor: accent,
                color: '#061018',
                '&:hover': { bgcolor: '#24f0a8' },
                '&:focus-visible': {
                  outline: `2px solid ${accent}`,
                  outlineOffset: 3,
                },
              }}
            >
              {copy.productCta}
            </Button>
          </GlassPanel>
        </Container>
      </Box>

      <PublicMarketingFooter />
      <PublicScrollToTopFab
        ariaLabel={locale === 'it' ? 'Torna su' : 'Back to top'}
      />
    </PageRoot>
  );
};
